import { Routing, CreateSwapRequest, TransactionFailureReason } from "uniswap/src/data/tradingApi/__generated__/index"
import { GasFeeResult, ValidatedGasFeeResult, validateGasFeeResult } from "uniswap/src/features/gas/types"
import { BridgeTrade, ClassicTrade, IndicativeTrade, UniswapXTrade } from "uniswap/src/features/transactions/swap/types/trade"
import { isBridge, isClassic, isUniswapX } from "uniswap/src/features/transactions/swap/utils/routing"
import { ValidatedPermit, ValidatedTransactionRequest } from "uniswap/src/features/transactions/swap/utils/trade"
import { GasFeeEstimates } from "uniswap/src/features/transactions/types/transactionDetails"
import { isInterface } from "utilities/src/platform"

export type SwapTxAndGasInfo = ClassicSwapTxAndGasInfo | UniswapXSwapTxAndGasInfo | BridgeSwapTxAndGasInfo
export type ValidatedSwapTxContext = ValidatedClassicSwapTxAndGasInfo | ValidatedUniswapXSwapTxAndGasInfo | ValidatedBridgeSwapTxAndGasInfo

export function isValidSwapTxContext(swapTxContext: SwapTxAndGasInfo | unknown): swapTxContext is ValidatedSwapTxContext {
  // Validation fn prevents/future-proofs typeguard against illicit casts
  return validateSwapTxContext(swapTxContext) !== undefined
}

export type SwapGasFeeEstimation = {
  swapEstimates?: GasFeeEstimates
  approvalEstimates?: GasFeeEstimates
  wrapEstimates?: GasFeeEstimates
}

export type UniswapXGasBreakdown = {
  classicGasUseEstimateUSD?: string
  approvalCost?: string
  wrapCost?: string
  inputTokenSymbol?: string
}

interface BaseSwapTxAndGasInfo {
  routing: Routing
  trade?: ClassicTrade | UniswapXTrade | BridgeTrade
  indicativeTrade: IndicativeTrade | undefined
  approveTxRequest: ValidatedTransactionRequest | undefined
  permit: ValidatedPermit | undefined
  revocationTxRequest: ValidatedTransactionRequest | undefined
  gasFee: GasFeeResult
  gasFeeEstimation: SwapGasFeeEstimation
}

export interface ClassicSwapTxAndGasInfo extends BaseSwapTxAndGasInfo {
  routing: Routing.CLASSIC
  trade?: ClassicTrade
  swapRequestArgs: CreateSwapRequest | undefined
  simulationErrors?: TransactionFailureReason[]
  /**
   * `unsigned` is true if `txRequest` is undefined due to a permit signature needing to be signed first.
   * This occurs on interface where the user must be prompted to sign a permit before txRequest can be fetched.
  */
  unsigned: boolean
  txRequest: ValidatedTransactionRequest | undefined
}

export interface UniswapXSwapTxAndGasInfo extends BaseSwapTxAndGasInfo {
  routing: Routing.DUTCH_V2 | Routing.PRIORITY
  trade: UniswapXTrade
  wrapTxRequest: ValidatedTransactionRequest | undefined
  gasFeeBreakdown: UniswapXGasBreakdown
}

export interface BridgeSwapTxAndGasInfo extends BaseSwapTxAndGasInfo {
  routing: Routing.BRIDGE
  trade: BridgeTrade
  indicativeTrade: undefined
  swapRequestArgs: CreateSwapRequest | undefined
  gasFeeEstimation: SwapGasFeeEstimation

  /**
   * `unsigned` is true if `txRequest` is undefined due to a permit signature needing to be signed first.
   * This occurs on interface where the user must be prompted to sign a permit before txRequest can be fetched.
  */
  unsigned: boolean
  txRequest: ValidatedTransactionRequest | undefined
}

interface BaseRequiredSwapTxContextFields {
  gasFee: ValidatedGasFeeResult
}

export type ValidatedClassicSwapTxAndGasInfo = Omit<Required<ClassicSwapTxAndGasInfo>, 'simulationErrors'> & {
  simulationErrors?: ClassicSwapTxAndGasInfo['simulationErrors']
} & BaseRequiredSwapTxContextFields & ({
  unsigned: true
  permit: ValidatedPermit
  txRequest: undefined
} | {
  unsigned: false
  permit: undefined
  txRequest: ValidatedTransactionRequest
})

export type ValidatedBridgeSwapTxAndGasInfo = Required<BridgeSwapTxAndGasInfo> & BaseRequiredSwapTxContextFields & ({
  unsigned: true
  permit: ValidatedPermit
  txRequest: undefined
} | {
  unsigned: false
  permit: undefined
  txRequest: ValidatedTransactionRequest
})

export type ValidatedUniswapXSwapTxAndGasInfo = Required<UniswapXSwapTxAndGasInfo> & BaseRequiredSwapTxContextFields & {
  // Permit should always be defined for UniswapX orders
  permit: ValidatedPermit
}

function validateSwapTxContext(swapTxContext: SwapTxAndGasInfo | unknown): ValidatedSwapTxContext | undefined {
  
  if (!isSwapTx(swapTxContext)) {
    return undefined
  }


  const gasFee = validateGasFeeResult(swapTxContext.gasFee)

  // 🔧 DEBUG: Gas fee 验证信息
  const isGasFeeStillLoading = swapTxContext.gasFee?.isLoading && !swapTxContext.gasFee?.error
  
  // 🔧 关键修复：如果无需授权（approval === null 或 action === 'none'），可以没有 approval gas fee
  const noApprovalNeeded = !swapTxContext.tokenApprovalInfo || swapTxContext.tokenApprovalInfo?.action === 'none'
  
  // 🔧 关键修复：Permit2 离线签名不需要 gas fee（只是签名消息，不是链上交易）
  const isPermit2Signature = (swapTxContext as any).unsigned && !!(swapTxContext as any).permit
  
  // 🔧 允许以下情况没有 gas fee：
  // 1. 不需要授权 (noApprovalNeeded)
  // 2. Permit2 离线签名 (isPermit2Signature) - 只需要签名，不需要发交易
  // 3. Gas fee 还在加载中 (isGasFeeStillLoading)
  if (!gasFee && !isGasFeeStillLoading && !noApprovalNeeded && !isPermit2Signature) {
    return undefined
  }
  
  // 🔧 关键修复：当不需要授权时，即使没有 gas fee 也使用占位符（值为 '0'）
  // 这样可以通过验证，让交易继续进行
  const finalGasFee = gasFee || { 
    value: noApprovalNeeded ? '0' : '0', 
    displayValue: noApprovalNeeded ? '0' : '0', 
    error: null, 
    isLoading: isGasFeeStillLoading,
    params: undefined,
    gasEstimates: undefined
  }

  if (swapTxContext.trade) {
    if (isClassic(swapTxContext)) {
      const { trade, txRequest, unsigned, permit } = swapTxContext


      if (unsigned) {
        // SwapTxContext should only ever be unsigned / still require a signature on interface.
        // For interface with permit2, we allow permitData even without signature yet
        const hasPermitData = !!(swapTxContext as any).permitData
        const hasApproveTxRequest = !!(swapTxContext as any).approveTxRequest
        

        
        // 🔧 关键修复：当有 approval 需要处理时，即使没有 permit 也应该验证通过
        if (isInterface && hasApproveTxRequest) {
          return { 
            ...swapTxContext, 
            trade, 
            gasFee: finalGasFee, 
            unsigned, 
            txRequest: undefined, 
            permit: permit || undefined 
          }
        }
        
        if (!isInterface || (!permit && !hasPermitData)) {
          return undefined
        }
        return { ...swapTxContext, trade, gasFee: finalGasFee, unsigned, txRequest: undefined, permit: permit! }
      } else if (txRequest) {
        return { ...swapTxContext, trade, gasFee: finalGasFee, unsigned, txRequest, permit: undefined, }
      } else {
        // 🔧 TIMING FIX: Allow validation to pass if we're in permit2 flow even without txRequest yet
        const hasPermitData = !!(swapTxContext as any).permitData
        const hasApproveTxRequest = !!(swapTxContext as any).approveTxRequest
        
        if (isInterface && hasPermitData && permit) {
          return { ...swapTxContext, trade, gasFee: finalGasFee, unsigned: true, txRequest: undefined, permit }
        }
        
        // 🔧 关键修复：当有 approval 需要处理时，即使没有 swap txRequest 也应该验证通过
        if (isInterface && hasApproveTxRequest) {
          return { 
            ...swapTxContext, 
            trade, 
            gasFee: finalGasFee, 
            unsigned: true, 
            txRequest: undefined, 
            permit: undefined 
          }
        }
        
        // 🔧 关键修复：当不需要授权且 gas fee 还在加载时，返回有效上下文（允许按钮显示 loading 状态）
        if (isInterface && !hasPermitData && isGasFeeStillLoading) {
          // 如果不需要授权，即使在加载中也返回有效上下文
          if (noApprovalNeeded) {
            return { 
              ...swapTxContext, 
              trade, 
              gasFee: finalGasFee, 
              unsigned: true, 
              txRequest: undefined, 
              permit: undefined 
            }
          }
          return undefined // 需要授权但还在加载，继续等待
        }
        
        // 🔧 新增：当 gas fee 已加载但仍无 txRequest 时，可能是 API 调用失败或延迟
        if (isInterface && !hasPermitData && !isGasFeeStillLoading) {
          // 🔧 关键修复：当有 gas fee 但没有 txRequest 时，返回一个临时有效状态
          // 这允许按钮保持可点击状态，而不是被禁用
          if (swapTxContext.gasFee?.value || noApprovalNeeded) {
            return { 
              ...swapTxContext, 
              trade, 
              gasFee: finalGasFee, 
              unsigned: true, 
              txRequest: undefined, 
              permit: undefined 
            }
          }
          // 如果连 gas fee 都没有，则继续等待
          return undefined
        }
        
        // 🔧 最后的兜底：如果不需要授权，总是返回有效上下文
        if (isInterface && noApprovalNeeded) {
          return { 
            ...swapTxContext, 
            trade, 
            gasFee: finalGasFee, 
            unsigned: true, 
            txRequest: undefined, 
            permit: undefined 
          }
        }
        
        return undefined
      }

    } else if (isBridge(swapTxContext)  && swapTxContext.txRequest) {
      const { trade, txRequest, unsigned, permit } = swapTxContext


      if (unsigned) {
        // SwapTxContext should only ever be unsigned / still require a signature on interface.
        if (!isInterface || !permit) {
          return undefined
        }
        return { ...swapTxContext, trade, gasFee: finalGasFee, unsigned, txRequest: undefined, permit }
      } else if (txRequest) {
        return { ...swapTxContext, trade, gasFee: finalGasFee, unsigned, txRequest, permit: undefined }
      }
    } else if (isUniswapX(swapTxContext) && swapTxContext.permit) {
      const { trade, permit } = swapTxContext

      return { ...swapTxContext, trade, gasFee: finalGasFee, permit }
    }
  } 
  
  return undefined
}

function isSwapTx(swapTxContext: unknown): swapTxContext is SwapTxAndGasInfo {
  return typeof swapTxContext === 'object' && swapTxContext !== null && 'trade' in swapTxContext && 'routing' in swapTxContext;
}
